import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  getDateFromPixelPosition,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import { addMilliseconds, differenceInMilliseconds, set } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { TimelineCellHighlightService } from '@wilson/shift-timeline/assignments-gantt-chart';
import { MoveAbsenceHelperService } from '../move-absence-drop-zone/move-absence-helper.service';

@Component({
  selector: 'wilson-move-absence-drop-zone-cell-view',
  imports: [CommonModule],
  template: `<div
    class="drop-zone"
    [style.left.px]="resourcesColumnWidthPx$ | async"
  ></div>`,
  styleUrls: ['./move-absence-drop-zone-cell-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttDateToPositionPxPipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    MoveAbsenceHelperService,
  ],
})
export class MoveAbsenceDropZoneCellViewComponent
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly timelineCellHighlightService = inject(
    TimelineCellHighlightService,
  );
  private readonly moveAbsenceHelperService = inject(MoveAbsenceHelperService);

  public resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );

  async ngOnInit(): Promise<void> {
    await this.moveAbsenceHelperService.setAbsenceWidth();
  }

  protected async onDragOverHandler(offsetX: number) {
    this.timelineCellHighlightService.clearCellsFromHighlight();

    const draggingAbsence = await firstValueFrom(
      this.dragAndDropModeService.draggingAbsence$,
    );
    if (draggingAbsence) {
      const dateFromPx = getDateFromPixelPosition({
        pixelPosition: offsetX,
        timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
      });
      const timeDifference = differenceInMilliseconds(
        new Date(draggingAbsence.absentTo),
        new Date(draggingAbsence.absentFrom),
      );
      const newStartDate = set(dateFromPx, {
        hours: new Date(draggingAbsence.absentFrom).getHours(),
        minutes: new Date(draggingAbsence.absentFrom).getMinutes(),
      });
      const mockAbsence = {
        ...draggingAbsence,
        absentFrom: newStartDate.toISOString(),
        absentTo: addMilliseconds(newStartDate, timeDifference).toISOString(),
      };

      await this.moveAbsenceHelperService.onDragOverHandler(mockAbsence);

      this.timelineCellHighlightService.setCellToHighlight(
        mockAbsence.userId as string,
        mockAbsence.absentFrom,
      );
    }
  }

  protected async onDragLeaveHandler() {
    await this.moveAbsenceHelperService.onDragLeaveHandler();
    this.timelineCellHighlightService.clearCellsFromHighlight();
  }

  protected async onDropHandler() {
    await this.moveAbsenceHelperService.onDropHandler();
    this.timelineCellHighlightService.clearCellsFromHighlight();
  }
}
